import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';

import Dialog from 'components/Dialog';
import { apiErrorHandler } from 'core/errors';
import { Dispatch } from 'redux';
import { UPDATE_ALL_COURSES } from 'redux/actions';
import { API, getCancelToken, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';

import { Button, Tooltip } from '../UtilElements';

type TermOption = { label: string; value: UniversityTerm };
const LOAD_MORE_TERMS = 'Load More Terms';

interface FetchAllCoursesResponse extends MessageResponse {
  defaultTermSet: NumericBoolean;
  courses: AvailableCourse[];
  terms: UniversityTerm[];
  requiresApproval: NumericBoolean;
}

interface FetchUniversityTermsResponse extends MessageResponse {
  termData: UniversityTerm[];
}

interface ApproveCourseResponse extends MessageResponse {
  courseId: string;
}

const mapState = (state: RootState) => ({
  courses: state.allCourses,
});

const mapDispatch = (dispatch: Dispatch) => ({
  updateAllCourses: async (data: AvailableCourse[]) => {
    dispatch({
      type: UPDATE_ALL_COURSES,
      payload: data,
    });
  },
});

type Props = ToastManagerProp & ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

interface AllCoursesState {
  isLoading: boolean;
  isLoadingTerms: boolean;
  isCourseCreating: boolean;
  isScrollDisabled: boolean;
  courseScreen: CourseScreen;
  importingCourseId: string;
  terms: UniversityTerm[];
  requiresApproval: NumericBoolean;
  selectedTerm: UniversityTerm | null;
  showLoadMoreTermDialog: boolean;
  isApprovingCourse: HashMap<boolean>;
}

class AllCourses extends Component<Props, AllCoursesState> {
  apiSources = getCancelToken();

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      isLoadingTerms: false,
      isCourseCreating: false,
      isScrollDisabled: false,
      courseScreen: 'loading',
      importingCourseId: '',
      terms: [],
      requiresApproval: 0,
      selectedTerm: null,
      showLoadMoreTermDialog: false,
      isApprovingCourse: {},
    };
  }

  fetchAllCourses() {
    return request<FetchAllCoursesResponse>({
      url: API.COURSES_ALL,
      method: 'GET',
      cancelToken: this.apiSources.token,
    });
  }

  fetchUniversityTerms() {
    return request<FetchUniversityTermsResponse>({
      url: API.GET_TERMS,
      method: 'GET',
      cancelToken: this.apiSources.token,
    });
  }

  setDefaultTerm(term: UniversityTerm) {
    return request<FetchAllCoursesResponse>({
      url: API.SET_DEFAULT_TERM,
      method: 'POST',
      data: { termId: term.termId },
    });
  }

  approveCourse(course: AvailableCourse) {
    return request<ApproveCourseResponse>({
      url: API.COURSE_APPROVE_REQUEST,
      method: 'POST',
      data: { courseId: course.courseId, code: course.code, title: course.title },
    });
  }

  async componentDidMount() {
    this.apiSources = getCancelToken();
    try {
      const { courses, terms, requiresApproval } = await this.fetchAllCourses();
      const defaultTerm = terms.find((t) => !!t.isDefault);
      this.props.updateAllCourses(courses);
      this.setState({
        terms,
        requiresApproval,
        isLoading: false,
        selectedTerm: defaultTerm || null,
      });
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ isLoading: false });
      });
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
  }

  async handleApproveCourse(course: AvailableCourse) {
    this.setState({ isApprovingCourse: { courseId: true } });
    await this.approveCourse(course);
    this.setState({ isApprovingCourse: { courseId: false } });

    this.props.updateAllCourses(
      this.props.courses.map((c) => {
        if (c.courseId !== course.courseId) return c;
        return {
          ...c,
          approved: 1,
          approvalRequested: 0,
        };
      })
    );
  }

  getRow = (course: AvailableCourse) => {
    let action: ReactNode;

    if (course.isImported) {
      action = <div className='bold import table__cell-value'>IMPORTED</div>;
    } else if (course.approved || !this.state.requiresApproval) {
      action = (
        <Link
          to={{ pathname: ROUTES.ADMIN_COURSES_CREATE, state: { courseId: course.courseId } }}
          component={({ navigate, href }) => {
            return (
              <Button
                type='ghost'
                selector='btn-small import'
                label='IMPORT'
                onRipple={() => navigate(href)}
              />
            );
          }}
        />
      );
    } else if (course.approvalRequested) {
      action = (
        <Button
          type='ghost'
          selector='btn-small approve-course-button'
          label='APPROVE'
          disabled={this.state.isApprovingCourse[course.courseId]}
          onRipple={() => this.handleApproveCourse(course)}
        />
      );
    } else {
      action = (
        <Button
          type='ghost'
          selector='btn-small approve-course-button'
          label='PRE APPROVE'
          disabled={this.state.isApprovingCourse[course.courseId]}
          onRipple={() => this.handleApproveCourse(course)}
        />
      );
    }

    return (
      <tr key={course.code}>
        <td className='table__cell'>{course.code}</td>
        <td className='table__cell'>
          <Tooltip label={course.title} charLimit={50} />
        </td>
        <td className='table__cell'>{action}</td>
      </tr>
    );
  };

  showLoadMoreTermsDialog = async () => {
    try {
      this.setState({ isLoadingTerms: true, showLoadMoreTermDialog: true });
      const { termData } = await this.fetchUniversityTerms();
      this.setState({ isLoadingTerms: false, terms: termData });
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ isLoadingTerms: false });
      });
    }
  };

  selectDefaultTerm = async (term: UniversityTerm) => {
    try {
      this.setState({ isLoading: true, showLoadMoreTermDialog: false });
      const { courses, terms, requiresApproval } = await this.setDefaultTerm(term);
      this.props.updateAllCourses(courses);
      this.setState({
        terms,
        requiresApproval,
        isLoading: false,
        selectedTerm: term,
      });
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err, () => {
        this.setState({ isLoading: false });
      });
    }
  };

  onTermChange = async (option: TermOption | TermOption[] | null | undefined) => {
    if (!option) {
      this.setState({ selectedTerm: null });
      return;
    }

    option = Array.isArray(option) ? option[0] : option;

    if (option.label === LOAD_MORE_TERMS) {
      this.showLoadMoreTermsDialog();
      return;
    }

    this.selectDefaultTerm(option.value);
  };

  render() {
    const { courses } = this.props;
    const { isLoading, isLoadingTerms, terms, selectedTerm, showLoadMoreTermDialog } = this.state;
    return (
      <div className='section' key='page'>
        <div className='section__header'>
          <h3 className='section__title'>
            {isLoading
              ? 'Loading courses...'
              : courses.length
              ? `All available courses for term: ${selectedTerm?.name}`
              : `No courses available for term: ${selectedTerm?.name}`}
          </h3>
          <Select
            className='term-selector'
            placeholder='Select term'
            options={terms
              .map((t) => ({ label: t.name, value: t }))
              .concat([
                {
                  label: LOAD_MORE_TERMS,
                  value: {
                    termId: '',
                    isDefault: 0,
                    isImported: 0,
                    name: LOAD_MORE_TERMS,
                  },
                },
              ])}
            value={selectedTerm ? { label: selectedTerm.name, value: selectedTerm } : undefined}
            onChange={this.onTermChange as any}
          />
        </div>
        <div className='section__body'>
          <table className='table table--bordered'>
            <thead>
              <tr>
                <th className='table__cell table__cell--left'>Course Code</th>
                <th className='table__cell table__cell--left'>Title</th>
                <th className='table__cell' />
              </tr>
            </thead>
            <tbody>{courses.map(this.getRow)}</tbody>
          </table>
        </div>
        <Dialog
          variant='small'
          isOpen={showLoadMoreTermDialog}
          title='Select Course Term'
          footer={
            <Button
              label='Cancel'
              type='success'
              onRipple={() => this.setState({ showLoadMoreTermDialog: false })}
            />
          }
        >
          {(modalTargetEl) => (
            <Select
              isLoading={isLoadingTerms}
              placeholder='Select term'
              classNamePrefix='react-select'
              onChange={this.onTermChange as any}
              options={terms.map((t) => ({ label: t.name, value: t }))}
              menuPortalTarget={modalTargetEl}
              value={selectedTerm ? { label: selectedTerm.name, value: selectedTerm } : undefined}
            />
          )}
        </Dialog>
      </div>
    );
  }
}

export default connect(mapState, mapDispatch)(withToastManager(AllCourses));
